// ── 早期雷达 · Early Bird 子 tab ─────────────────────────────────────
// 数据源：earlybird_projects 表（scripts/ingest-earlybird.mjs 灌入的快照）。
// 结构：Web3/非Web3 切换 → 合并分类导航（产品赛道 + 生态建设 chips）→ 卡片列表。
// 卡片 v1 只展示已有真实数据的维度；叙事/团队/运营等待 AI 画像与 GitHub
// enrichment 单元接入前，明确标「待接入」，不用占位数据冒充。
import { useState, useEffect } from 'react';
import { ExternalLink, Lock, Star, UserCheck, X, Flame } from 'lucide-react';
import { useLang, useT } from '../i18n';
import { useAuth } from '../hooks/useAuth';
import { useNotes } from '../hooks/useNotes';
import { supabase } from '../lib/supabase';
import { fetchEarlyBirdCounts, fetchEarlyBirdProjects, fetchEarlyBirdPrograms, fetchEarlyBirdProjectById, fetchWikiBriefsForIds } from '../services/earlyBirdFeed';
import { ECOSYSTEM_CATEGORY_LABELS_EN } from '../services/earlyBirdTaxonomy';
import { fetchSignalsForHandles } from '../services/investorSignals';
import { Eye } from 'lucide-react';
import { resolveEarlyBirdContact } from '../services/earlyBird';
import { parseEarlyBirdShareId } from '../utils/earlyBirdShareHash';
import { EarlyBirdWikiModal } from './EarlyBirdWikiModal';
import { WikiFoundersMention } from './EarlyBirdWikiFoundersMention';
import { EarlyBirdWeeklyDigest, OpenDigestButton } from './EarlyBirdWeeklyDigest';
import { ADMIN_UID } from '../constants';

const FREE_CREDITS = 3;
const FOLLOW_KEY = 'earlybird_follows';

// 与 App.jsx 的 C 保持一致（模块内常量未导出，这里镜像用到的值）
const C = {
  surface: '#ffffff', border: '#e4e4e7', text: '#18181b',
  muted: '#71717a', faint: '#f4f4f5',
};
const ECO = { bg: '#e1f5ee', border: '#5dcaa5', text: '#085041' };

const PAGE_SIZE = 20;

const chipStyle = (active, isEco = false) => ({
  padding: '4px 12px', borderRadius: 999, fontSize: 11, cursor: 'pointer',
  background: active ? C.text : isEco ? ECO.bg : C.surface,
  border: `1px solid ${active ? C.text : isEco ? ECO.border : C.border}`,
  color: active ? '#fff' : isEco ? ECO.text : C.muted,
  fontWeight: active ? 600 : 400,
});

const rowLabelStyle = {
  color: C.muted, fontSize: 9.5, fontWeight: 700, minWidth: 26,
  flexShrink: 0, marginTop: 1,
};
const rowStyle = { display: 'flex', gap: 6, alignItems: 'flex-start', lineHeight: 1.5, marginBottom: 3 };

function CardRow({ label, children }) {
  return (
    <div style={rowStyle}>
      <span style={rowLabelStyle}>{label}</span>
      <span style={{ flex: 1, fontSize: 11.5 }}>{children}</span>
    </div>
  );
}

function Pending({ lang, what }) {
  return (
    <span style={{ color: '#a1a1aa', fontStyle: 'italic' }}>
      {lang === 'en' ? `Pending (${what})` : `待接入（${what}）`}
    </span>
  );
}

const NoData = ({ children }) => <span style={{ color: '#a1a1aa' }}>{children}</span>;

// 项目图标：三级兜底。
// 1. logo_url —— 项目方自己的真实 logo，目前只有 Colosseum 有（scrape-colosseum.mjs
//    抓取时带出 image 字段，见 earlybird_projects.logo_url）。
// 2. GitHub 头像 —— 解析出仓库的项目（不分来源），GitHub 对任意合法用户名都会返回
//    真实头像或默认 identicon，免费、不用额外抓取。
// 3. 官网 favicon —— ETHGlobal Showcase/EF ESP 的上游数据集 schema 里根本没有
//    logo/image 字段（实测确认过，见 docs.early-bird.space 的 schema 文档），大部分
//    项目也没有公开仓库，只能退而用项目自己网站的 favicon。用 DuckDuckGo 的公开图标
//    代理（icons.duckduckgo.com/ip3/{host}.ico）而不是自己抓网页解析 <link rel="icon">
//    ——favicon 声明方式五花八门（多种格式/尺寸/apple-touch-icon），自己解析既麻烦
//    又不可靠，这个服务本来就是干这个的，网站没有 favicon 时会正常返回 404（不是拿到
//    一张假图片），方便 onError 处理。
// 每一级解析不出来或图片加载失败都直接不显示图标，不占位。
function faviconUrl(projectLiveUrl) {
  if (!projectLiveUrl) return null;
  try {
    const host = new URL(projectLiveUrl).hostname;
    return host ? `https://icons.duckduckgo.com/ip3/${host}.ico` : null;
  } catch { return null; }
}

function githubAvatarUrl(githubUrl) {
  if (!githubUrl) return null;
  const m = String(githubUrl).match(/github\.com\/([\w.-]+)/i);
  return m ? `https://github.com/${m[1]}.png?size=64` : null;
}

// 按优先级试一串候选图标 URL，某一级加载失败（onError，包括 favicon 服务返回 404）
// 就退到下一级；全部失败就不渲染任何东西，不留占位图标
function ProjectIcon({ candidates }) {
  const [i, setI] = useState(0);
  const src = candidates[i];
  if (!src) return null;
  return (
    <img
      key={src} src={src} alt="" width={28} height={28}
      style={{ borderRadius: 6, flexShrink: 0, marginTop: 1 }}
      onError={() => setI((n) => n + 1)}
    />
  );
}

// 团队行：top contributors（enrichment 脚本写入的 GitHub 真实数据）
// 三种空态要区分：尚未跑 enrichment（待接入）/ 跑过但没解析到仓库 / 仓库不可访问
function TeamRow({ p, lang }) {
  if (!p.github_checked_at) return <Pending lang={lang} what={lang === 'en' ? 'GitHub data' : 'GitHub 数据'} />;
  if (!p.github_url) return <NoData>{lang === 'en' ? 'No public repo link' : '无公开仓库链接'}</NoData>;
  const contributors = p.github_top_contributors || [];
  if (contributors.length === 0) return <NoData>{lang === 'en' ? 'Repo not accessible' : '仓库不可访问'}</NoData>;
  const [top, ...rest] = contributors;
  const restNames = rest.map((c) => c.login).join('、');
  return (
    <>
      {top.login}{lang === 'en' ? ` leads (${top.contributions} commits)` : ` 主导（${top.contributions} 次提交）`}
      {restNames ? (lang === 'en' ? ` · with ${restNames}` : ` · ${restNames} 参与`) : ''}
    </>
  );
}

// 运营行：stars + 最近提交距今天数（pilot 结论：黑客松项目 star 普遍为 0，
// 「多久没提交」才是还在做 vs 已弃坑的真信号，所以提交时间放前面判断颜色）
// 无公开仓库/仓库不可访问时，用 site_alive（项目自己的网站是否还能打开）兜底——
// 这个信号来自同一次 enrichment 抓取顺带拿到的项目自有域名，见 enrich-earlybird-github.mjs。
function OpsRow({ p, lang }) {
  const hasGithubStats = p.github_url && p.github_stars != null;
  if (hasGithubStats) {
    const days = p.github_pushed_at
      ? Math.max(0, Math.floor((Date.now() - new Date(p.github_pushed_at).getTime()) / 86400000))
      : null;
    const pushText = days == null
      ? (lang === 'en' ? 'no push data' : '无提交记录')
      : days <= 60
        ? (lang === 'en' ? `pushed ${days}d ago` : `${days} 天前有提交`)
        : (lang === 'en' ? `inactive ${Math.floor(days / 30)}mo` : `${Math.floor(days / 30)} 个月无提交`);
    const active = days != null && days <= 60;
    return (
      <span style={{ color: active ? '#15803d' : days != null ? '#c2410c' : undefined }}>
        GitHub {p.github_stars.toLocaleString()}★ · {pushText}
      </span>
    );
  }
  if (p.site_checked_at) {
    return (
      <span style={{ color: p.site_alive ? '#15803d' : '#c2410c' }}>
        {p.site_alive
          ? (lang === 'en' ? 'Project site reachable' : '项目网站可访问')
          : (lang === 'en' ? 'Project site unreachable' : '项目网站无法访问')}
      </span>
    );
  }
  if (!p.github_checked_at) return <Pending lang={lang} what={lang === 'en' ? 'GitHub data' : 'GitHub 数据'} />;
  if (!p.github_url) return <NoData>{lang === 'en' ? 'No public repo link' : '无公开仓库链接'}</NoData>;
  return <NoData>{lang === 'en' ? 'Repo not accessible' : '仓库不可访问'}</NoData>;
}

const NOT_FOUND_RE = /^未搜索到|^no information/i;
const truncate = (s, n) => (s.length > n ? `${s.slice(0, n).trim()}…` : s);

// AI 综合叙事的通用展示——跟"团队/运营"那些硬事实（GitHub star/demo 存活）不是一回事,
// 这里的文本是 AI 基于网络搜索综合出来的,可能有误(同名实体、搜索结果不全等),只有已经
// 点过"查看档案"或被批量预热脚本覆盖过的项目才有数据。故意跟叙事/商业那两行的 AI
// 标签区分开(斜体 + 显式"未验证"字样),避免被当成和 GitHub 数据一样可信的事实直接采信。
// 原来这段(创始人+公司背景)单独占一行叫"画像",但两者主题不同——创始人信息应该跟
// 团队放一起看,公司背景应该跟融资放一起看,所以拆开塞进各自所属的行里,不再单独成行。
function AiUnverifiedNote({ text, lang, untranslated, t }) {
  if (!text) return null;
  return (
    <span style={{ display: 'block', marginTop: 3 }}>
      <span style={{ fontStyle: 'italic', color: '#71717a' }}>{truncate(text, 100)}</span>
      <span style={{
        fontSize: 9, padding: '1px 7px', marginLeft: 5, borderRadius: 999,
        background: '#f4f4f5', color: '#71717a', whiteSpace: 'nowrap',
      }}>{lang === 'en' ? 'AI · unverified' : 'AI 生成 · 未验证'}</span>
      {untranslated && t?.('misc.ai_content_note') && (
        <span style={{ color: '#71717a', fontSize: 9, marginLeft: 5 }}>{t('misc.ai_content_note')}</span>
      )}
    </span>
  );
}

// 人才背景信号：GitHub bio/company 关键词直接命中原文（detectPedigreeSignal），跟上面
// WikiBriefRow 的 AI 综合叙事不是一回事——这里没有 AI 推断，只呈现当事人自己公开写的
// 信息，所以刻意不带"AI 生成 · 未验证"标签，用不同的（紫色实心）视觉处理区分可信度。
// 只有已经点过"查看档案"、且真的查到信号的项目才会显示，其余项目这一行不出现。
function PedigreeRow({ hits, lang }) {
  if (!hits?.length) return null;
  return (
    <CardRow label={lang === 'en' ? 'Talent' : '人才'}>
      {hits.map((h, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, marginRight: 8 }}>
          <span style={{
            fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 700,
            background: '#faf5ff', color: '#9333ea',
          }}>{h.type === 'repeat_founder' ? '🔁' : '🏛'} {h.name}</span>
          <span style={{ color: C.muted, fontSize: 10.5 }}>{h.evidence}</span>
        </span>
      ))}
    </CardRow>
  );
}

export function EarlyBirdTab() {
  const { lang } = useLang();
  const t = useT();
  const { user } = useAuth();
  const isAdmin = user?.id === ADMIN_UID;
  const { notes, addNote } = useNotes(user);
  const [view, setView] = useState('web3');          // 'web3' | 'nonweb3'
  const [counts, setCounts] = useState(null);
  const [category, setCategory] = useState(null);    // null = 全部
  const [organization, setOrganization] = useState(null); // null = 全部（仅 web3 池有意义：黑客松 vs 基金会资助）
  const [hasGithubOnly, setHasGithubOnly] = useState(false);
  const [siteAlive, setSiteAlive] = useState(null);     // null | 'alive' | 'dead' | 'unchecked'
  const [hasProfileOnly, setHasProfileOnly] = useState(false);
  const [highConfidenceOnly, setHighConfidenceOnly] = useState(false);
  const [program, setProgram] = useState(null);         // null = 全部场次
  const [programs, setPrograms] = useState([]);         // 当前 view 下的场次下拉选项
  const [sortBy, setSortBy] = useState('award_date'); // 'award_date' | 'github_stars' | 'recent_push'
  const [items, setItems] = useState([]);
  const [wikiBriefs, setWikiBriefs] = useState({}); // id → {founders, company}，仅已有缓存的项目才有，卡片低置信度预览用
  const [investorSignals, setInvestorSignals] = useState({}); // twitter_handle(小写) → 机构互动信号数组
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  // 联系方式解锁：已解锁项目 id + 审核通过的「我认识」加成额度
  const [unlockedIds, setUnlockedIds] = useState(new Set());
  const [approvedTips, setApprovedTips] = useState(0);
  const [contactMap, setContactMap] = useState({}); // id → {status:'loading'|'shown'|'none', contact}
  const [hint, setHint] = useState('');             // 底部操作提示（未登录/额度用完）
  const [follows, setFollows] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FOLLOW_KEY) || '[]')); } catch { return new Set(); }
  });
  const [tipModal, setTipModal] = useState(null);   // { project, contact, relationship, state }
  const [wikiProject, setWikiProject] = useState(null); // 当前打开 wiki 档案弹窗的项目
  const [showDigest, setShowDigest] = useState(false); // 周报草稿面板是否展开

  // ── 可分享深链：#earlybird/:id ──────────────────────────────────────
  // 挂载时检测一次 hash，命中就直接按 id 查这条项目并打开弹窗（不知道它在分页/
  // 筛选后的第几页，按 id 查最简单）。只在挂载时跑一次——用户在页面内点别的项目
  // 打开弹窗走的是下面 openWiki，不经过这里。
  useEffect(() => {
    const shareId = parseEarlyBirdShareId(window.location.hash);
    if (!shareId) return;
    fetchEarlyBirdProjectById(shareId).then((p) => { if (p) setWikiProject(p); });
  }, []); // eslint-disable-line

  // 弹窗打开/关闭时同步 hash，让"当前正看的这个项目"随时可分享
  // （App.jsx 顶层的 hash-sync effect 已经加了例外，不会把这个 hash 覆盖掉）
  useEffect(() => {
    if (wikiProject) {
      window.location.hash = `earlybird/${encodeURIComponent(wikiProject.id)}`;
    } else if (parseEarlyBirdShareId(window.location.hash)) {
      window.location.hash = 'pipeline';
    }
  }, [wikiProject]);

  useEffect(() => {
    fetchEarlyBirdCounts().then(setCounts).catch(() => setCounts(null));
  }, []);

  useEffect(() => {
    fetchEarlyBirdPrograms(view === 'web3').then(setPrograms).catch(() => setPrograms([]));
  }, [view]);

  useEffect(() => {
    if (!user || !supabase) { setUnlockedIds(new Set()); setApprovedTips(0); return; }
    supabase.from('earlybird_contact_unlocks').select('project_id').then(({ data }) => {
      if (data) setUnlockedIds(new Set(data.map((r) => r.project_id)));
    });
    supabase.from('earlybird_contact_tips').select('id').eq('status', 'approved').then(({ data }) => {
      if (data) setApprovedTips(data.length);
    });
  }, [user]);

  // 管理员账号不受免费额度限制（解锁记录仍照常写库留痕，只是不卡在门槛上）
  const creditsLeft = isAdmin ? Infinity : Math.max(0, FREE_CREDITS + approvedTips - unlockedIds.size);

  const toggleFollow = (id) => {
    setFollows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(FOLLOW_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const unlockContact = async (p) => {
    if (!user) { setHint(lang === 'en' ? 'Sign in to get contact info (3 free)' : '请先登录 — 登录用户有 3 次免费额度'); return; }
    const alreadyUnlocked = unlockedIds.has(p.id);
    if (!alreadyUnlocked && creditsLeft <= 0) {
      setHint(lang === 'en'
        ? 'No credits left — contribute a contact via "I know them" to earn +1'
        : '免费额度已用完 — 通过「我认识」提供一个项目联系方式，审核通过后 +1 额度');
      return;
    }
    setHint('');
    setContactMap((m) => ({ ...m, [p.id]: { status: 'loading' } }));
    const result = await resolveEarlyBirdContact(p.source_url, p.project_live_url, p).catch(() => null);
    const contact = result?.contact || null;
    // wiki 创始人段落是 AI 从网络搜索综合出来的，不是本人自愿公开的联系渠道——跟上面
    // email/twitter/team 这些"硬验证"字段不是一回事，只作为补充线索单独展示、明确标
    // "未验证"，不计入是否扣额度的判断（额度只认真正验证过的联系渠道）
    const wikiFoundersRaw = wikiBriefs[p.id]?.founders && !NOT_FOUND_RE.test(wikiBriefs[p.id].founders)
      ? wikiBriefs[p.id].founders : null;
    const wikiFounders = lang === 'en'
      ? (wikiBriefs[p.id]?.foundersEn || wikiFoundersRaw)
      : wikiFoundersRaw;
    if (!contact) {
      // 没查到公开联系方式不扣额度
      setContactMap((m) => ({ ...m, [p.id]: { status: 'none', wikiFounders } }));
      return;
    }
    if (!alreadyUnlocked && supabase) {
      const { error } = await supabase.from('earlybird_contact_unlocks')
        .insert({ user_id: user.id, project_id: p.id });
      if (!error) setUnlockedIds((prev) => new Set([...prev, p.id]));
    }
    setContactMap((m) => ({ ...m, [p.id]: { status: 'shown', contact, wikiFounders } }));
  };

  const submitTip = async () => {
    if (!tipModal || !user || !supabase) return;
    if (!tipModal.contact?.trim()) return;
    setTipModal((t) => ({ ...t, state: 'submitting' }));
    const { error } = await supabase.from('earlybird_contact_tips').insert({
      user_id: user.id,
      project_id: tipModal.project.id,
      contact_info: tipModal.contact.trim(),
      relationship: tipModal.relationship?.trim() || null,
    });
    setTipModal((t) => ({ ...t, state: error ? 'error' : 'done' }));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setWikiBriefs({});
    fetchEarlyBirdProjects({
      isWeb3: view === 'web3', category, organization: view === 'web3' ? organization : null,
      hasGithub: hasGithubOnly, siteAlive, hasProfile: hasProfileOnly,
      taxConfidence: highConfidenceOnly ? 'high' : null, program, sortBy, page, pageSize: PAGE_SIZE,
    })
      .then(({ items: rows, total: n }) => {
        if (cancelled) return;
        setItems(rows);
        setTotal(n);
        // 只读已有缓存做卡片预览，不触发生成——见 fetchWikiBriefsForIds 注释
        fetchWikiBriefsForIds(rows.map((p) => p.id)).then((briefs) => { if (!cancelled) setWikiBriefs(briefs); });
        // 机构互动信号——数据由 scripts/match-investor-signals.mjs 批量写入，这里只读
        const handles = rows.map((p) => p.twitter_handle).filter(Boolean);
        fetchSignalsForHandles(handles).then((sigs) => { if (!cancelled) setInvestorSignals(sigs); });
      })
      .catch(() => { if (!cancelled) { setItems([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [view, category, organization, hasGithubOnly, siteAlive, hasProfileOnly, highConfidenceOnly, program, sortBy, page]);

  const selectView = (v) => { setView(v); setCategory(null); setOrganization(null); setProgram(null); setPage(0); };
  const selectCategory = (c) => { setCategory(c); setPage(0); };
  const selectOrganization = (o) => { setOrganization(o); setPage(0); };
  const selectSortBy = (s) => { setSortBy(s); setPage(0); };
  const toggleHasGithubOnly = () => { setHasGithubOnly((v) => !v); setPage(0); };
  const selectSiteAlive = (v) => { setSiteAlive(v); setPage(0); };
  const toggleHasProfileOnly = () => { setHasProfileOnly((v) => !v); setPage(0); };
  const toggleHighConfidenceOnly = () => { setHighConfidenceOnly((v) => !v); setPage(0); };
  const selectProgram = (v) => { setProgram(v || null); setPage(0); };

  const productEntries = counts ? Object.entries(counts.product).sort((a, b) => b[1] - a[1]) : [];
  const ecosystemEntries = counts ? Object.entries(counts.ecosystem).sort((a, b) => b[1] - a[1]) : [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      {/* Web3 / 非 Web3 切换 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${C.border}`, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 18 }}>
          {[
            ['web3', lang === 'en' ? 'Web3' : 'Web3 项目', counts?.web3Total],
            ['nonweb3', lang === 'en' ? 'Non-Web3' : '非 Web3 项目', counts?.nonWeb3Total],
          ].map(([key, label, n]) => (
            <button key={key} onClick={() => selectView(key)} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 2px 8px',
              fontSize: 13, fontWeight: view === key ? 600 : 400,
              color: view === key ? C.text : C.muted,
              borderBottom: `2px solid ${view === key ? C.text : 'transparent'}`,
            }}>
              {label}{n != null ? ` · ${n.toLocaleString()}` : ''}
            </button>
          ))}
        </div>
        {view === 'web3' && <OpenDigestButton onClick={() => setShowDigest(true)} lang={lang} />}
      </div>

      {showDigest && <EarlyBirdWeeklyDigest onClose={() => setShowDigest(false)} lang={lang} />}

      {/* 分类导航（仅 web3 池有分类；计数不可用时隐藏而不是显示 0） */}
      {view === 'web3' && counts && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
            <button onClick={() => selectCategory(null)} style={chipStyle(category === null)}>
              {lang === 'en' ? 'All' : '全部'} {counts.web3Total.toLocaleString()}
            </button>
            {productEntries.map(([name, n]) => (
              <button key={name} onClick={() => selectCategory(name)} style={chipStyle(category === name)}>
                {name} {n}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: C.muted, marginRight: 2 }}>
              {lang === 'en' ? 'Ecosystem' : '生态建设'}
            </span>
            {ecosystemEntries.map(([name, n]) => (
              <button key={name} onClick={() => selectCategory(name)} style={chipStyle(category === name, true)}>
                {lang === 'en' ? (ECOSYSTEM_CATEGORY_LABELS_EN[name] || name) : name} {n}
              </button>
            ))}
            <button onClick={() => selectCategory('Others')} style={chipStyle(category === 'Others')}>
              Others {counts.others}
            </button>
          </div>
        </div>
      )}

      {/* 排序 + 筛选工具条 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        {view === 'web3' && (
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              [null, lang === 'en' ? 'All sources' : '全部来源'],
              ['ETHGlobal Showcase', lang === 'en' ? 'Hackathon' : '黑客松'],
              ['Ethereum Foundation ESP', lang === 'en' ? 'EF grant' : '基金会资助'],
              ['Colosseum', lang === 'en' ? 'Accelerator' : '加速器'],
            ].map(([org, label]) => (
              <button key={label} onClick={() => selectOrganization(org)} style={chipStyle(organization === org)}>
                {label}
              </button>
            ))}
          </div>
        )}
        <label style={{ fontSize: 11, color: C.muted, display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
          <input type="checkbox" checked={hasGithubOnly} onChange={toggleHasGithubOnly} style={{ margin: 0 }} />
          {lang === 'en' ? 'Has GitHub repo only' : '只看有 GitHub 仓库'}
        </label>
        <label style={{ fontSize: 11, color: C.muted, display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
          <input type="checkbox" checked={hasProfileOnly} onChange={toggleHasProfileOnly} style={{ margin: 0 }} />
          {lang === 'en' ? 'Has AI profile only' : '只看已生成 AI 画像'}
        </label>
        <label style={{ fontSize: 11, color: C.muted, display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
          <input type="checkbox" checked={highConfidenceOnly} onChange={toggleHighConfidenceOnly} style={{ margin: 0 }} />
          {lang === 'en' ? 'High confidence only' : '只看高置信度分类'}
        </label>
        <select value={siteAlive || ''} onChange={(e) => selectSiteAlive(e.target.value || null)} style={{
          fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${C.border}`,
          background: C.surface, color: C.text, cursor: 'pointer',
        }}>
          <option value="">{lang === 'en' ? 'Project site: any' : '项目网站：不限'}</option>
          <option value="alive">{lang === 'en' ? 'Project site: reachable' : '项目网站：可访问'}</option>
          <option value="dead">{lang === 'en' ? 'Project site: unreachable' : '项目网站：无法访问'}</option>
          <option value="unchecked">{lang === 'en' ? 'Project site: unchecked' : '项目网站：未探测'}</option>
        </select>
        {programs.length > 0 && (
          <select value={program || ''} onChange={(e) => selectProgram(e.target.value)} style={{
            fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${C.border}`,
            background: C.surface, color: C.text, cursor: 'pointer', maxWidth: 220,
          }}>
            <option value="">{lang === 'en' ? 'Event: any' : '活动场次：不限'}</option>
            {programs.map(({ program: p, cnt }) => (
              <option key={p} value={p}>{p} ({cnt})</option>
            ))}
          </select>
        )}
        <select value={sortBy} onChange={(e) => selectSortBy(e.target.value)} style={{
          fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${C.border}`,
          background: C.surface, color: C.text, cursor: 'pointer',
        }}>
          <option value="award_date">{lang === 'en' ? 'Sort: Newest' : '排序：最新收录'}</option>
          <option value="momentum">{lang === 'en' ? 'Sort: Overall score' : '排序：综合评分'}</option>
          <option value="github_stars">{lang === 'en' ? 'Sort: Most stars' : '排序：GitHub Star 最多'}</option>
          <option value="recent_push">{lang === 'en' ? 'Sort: Recently active' : '排序：最近有更新'}</option>
        </select>
      </div>

      {hint && (
        <div style={{ marginBottom: 10, padding: '7px 12px', background: '#fef9c3', color: '#854d0e', borderRadius: 6, fontSize: 11.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{hint}</span>
          <button onClick={() => setHint('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#854d0e', display: 'flex' }} aria-label="close">
            <X size={12} strokeWidth={2} />
          </button>
        </div>
      )}

      {loading && <div style={{ color: C.muted, fontSize: 12, padding: '20px 0' }}>{lang === 'en' ? 'Loading…' : '加载中…'}</div>}

      {!loading && items.length === 0 && (
        <div style={{ color: C.muted, fontSize: 12, padding: '20px 0' }}>
          {lang === 'en' ? 'No projects found' : '没有找到项目'}
        </div>
      )}

      {/* 卡片列表 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((p) => {
          const isEco = p.tax_system === 'ecosystem';
          const isEF = p.organization === 'Ethereum Foundation ESP';
          const isAccelerator = p.organization === 'Colosseum';
          const brief = p.one_liner || p.description;
          // Early Bird 原始字段（one_liner/description）几乎全是英文（数据源是 ETHGlobal/
          // ESP/Colosseum 这类英文黑客松列表）。之前"技术"行在没有 ai_tech_zh 翻译时会
          // 直接把这段英文原文摊出来（只是打个"原始字段"标签，语言本身没做任何过滤），
          // 中文界面下等于原样看英文。跟 brief 语言不匹配时改成不显示，退到"—"。
          const briefIsZh = typeof brief === 'string' && /[一-龥]/.test(brief);
          const briefLangMatches = typeof brief === 'string' && (lang === 'en' ? !briefIsZh : briefIsZh);
          // wiki 缓存里的创始人/公司背景——分别用在下面团队行、融资行里，不再单独成"画像"行
          const wb = wikiBriefs[p.id];
          const wbFoundersRaw = wb?.founders && !NOT_FOUND_RE.test(wb.founders) ? wb.founders : null;
          const wbCompanyRaw = wb?.company && !NOT_FOUND_RE.test(wb.company) ? wb.company : null;
          const wbFounders = lang === 'en' ? (wb?.foundersEn || wbFoundersRaw) : wbFoundersRaw;
          const wbCompany = lang === 'en' ? (wb?.companyEn || wbCompanyRaw) : wbCompanyRaw;
          const wbFoundersUntranslated = lang === 'en' && wbFoundersRaw && !wb?.foundersEn;
          const wbCompanyUntranslated = lang === 'en' && wbCompanyRaw && !wb?.companyEn;
          // 图标候选优先级：logo_url（项目方真实 logo，目前只有 Colosseum）> GitHub 头像
          // （解析出仓库的项目）> 官网 favicon（兜底，覆盖没仓库也没 logo_url 的项目）
          const iconCandidates = [p.logo_url, githubAvatarUrl(p.github_url), faviconUrl(p.project_live_url)].filter(Boolean);
          return (
            <div key={p.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <ProjectIcon candidates={iconCandidates} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{p.project_name}</div>
                    <div style={{ fontSize: 10.5, color: C.muted, marginTop: 1 }}>
                    {p.organization}{p.award_date ? ` · ${p.award_date}` : ` · ${lang === 'en' ? 'date unknown' : '日期未知'}`}
                    {p.updated_at && (() => {
                      const d = Math.floor((Date.now() - new Date(p.updated_at).getTime()) / 86400000);
                      return ` · ${lang === 'en' ? `data updated ${d}d ago` : d === 0 ? '数据今天更新' : `数据 ${d} 天前更新`}`;
                    })()}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {(() => {
                    const sigs = p.twitter_handle ? investorSignals[p.twitter_handle.toLowerCase()] : null;
                    if (!sigs || sigs.length === 0) return null;
                    const names = [...new Set(sigs.map((s) => s.investor_handle))];
                    const title = lang === 'en'
                      ? `${names.map((n) => '@' + n).join(', ')} interacted with this project's tweets`
                      : `${names.map((n) => '@' + n).join('、')} 互动过这个项目的推文`;
                    return (
                      <span title={title}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700,
                          padding: '2px 8px', borderRadius: 999, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
                        }}>
                        <Eye size={11} strokeWidth={2.5} /> {lang === 'en' ? 'Institutional attention' : '机构关注中'}
                      </span>
                    );
                  })()}
                  {p.momentum_score != null && p.momentum_score >= 60 && (
                    <span title={lang === 'en' ? `Overall score ${p.momentum_score}/100` : `综合评分 ${p.momentum_score}/100`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700,
                        padding: '2px 8px', borderRadius: 999, background: '#FFF1E8', color: '#C2410C', border: '1px solid #FED7AA',
                      }}>
                      <Flame size={11} strokeWidth={2.5} /> {p.momentum_score}
                    </span>
                  )}
                  {view === 'web3' && p.tax_category !== 'Others' && (
                    <span style={{
                      fontSize: 10, padding: '2px 9px', borderRadius: 999,
                      background: isEco ? ECO.bg : C.faint,
                      color: isEco ? ECO.text : '#3f3f46',
                      border: `1px solid ${isEco ? ECO.border : C.border}`,
                    }}>{lang === 'en' ? (ECOSYSTEM_CATEGORY_LABELS_EN[p.tax_category] || p.tax_category) : p.tax_category}</span>
                  )}
                  {p.source_url && (
                    <a href={p.source_url} target="_blank" rel="noreferrer" title={p.source_url}
                      style={{ color: C.muted, display: 'flex' }}>
                      <ExternalLink size={13} strokeWidth={2} />
                    </a>
                  )}
                </div>
              </div>

              <CardRow label={lang === 'en' ? 'Story' : '叙事'}>
                {p.ai_narrative
                  ? <><span style={{ color: C.muted, fontWeight: 700, marginRight: 4, fontSize: 9 }}>AI</span>
                      {lang === 'en' ? (p.ai_narrative_en || p.ai_narrative) : p.ai_narrative}
                      {lang === 'en' && !p.ai_narrative_en && t('misc.ai_content_note') && (
                        <span style={{ color: C.muted, fontSize: 9, marginLeft: 5 }}>{t('misc.ai_content_note')}</span>
                      )}</>
                  : <Pending lang={lang} what={lang === 'en' ? 'AI profile' : 'AI 画像'} />}
              </CardRow>
              <CardRow label={lang === 'en' ? 'Tech' : '技术'}>
                {lang !== 'en' && p.ai_tech_zh
                  ? <>{p.ai_tech_zh.length > 160 ? `${p.ai_tech_zh.slice(0, 160).trim()}…` : p.ai_tech_zh}
                      <span style={{ color: C.muted, fontSize: 9, marginLeft: 5 }}>AI 翻译</span></>
                  : (brief && briefLangMatches)
                    ? <>{brief.length > 160 ? `${brief.slice(0, 160).trim()}…` : brief}
                        <span style={{ color: C.muted, fontSize: 9, marginLeft: 5 }}>
                          {lang === 'en' ? 'raw field' : '原始字段'}
                        </span></>
                    : <span style={{ color: '#a1a1aa' }}>—</span>}
              </CardRow>
              <CardRow label={lang === 'en' ? 'Biz' : '商业'}>
                {isEco
                  ? <>
                      {lang === 'en' ? 'Non-commercial — grant-funded work' : '非商业项目 — 资助类工作'}
                      <span style={{
                        fontSize: 9, padding: '1px 7px', marginLeft: 5, borderRadius: 999,
                        background: '#fef9c3', color: '#854d0e',
                      }}>{lang === 'en' ? 'not an investment target' : '非投资标的'}</span>
                    </>
                  : p.ai_business
                    ? <><span style={{ color: C.muted, fontWeight: 700, marginRight: 4, fontSize: 9 }}>AI</span>
                        {lang === 'en' ? (p.ai_business_en || p.ai_business) : p.ai_business}
                        {lang === 'en' && !p.ai_business_en && t('misc.ai_content_note') && (
                          <span style={{ color: C.muted, fontSize: 9, marginLeft: 5 }}>{t('misc.ai_content_note')}</span>
                        )}</>
                    : <Pending lang={lang} what={lang === 'en' ? 'AI profile' : 'AI 画像'} />}
              </CardRow>
              <CardRow label={lang === 'en' ? 'Team' : '团队'}>
                <TeamRow p={p} lang={lang} />
                <AiUnverifiedNote text={wbFounders} lang={lang} untranslated={wbFoundersUntranslated} t={t} />
              </CardRow>
              <PedigreeRow hits={wikiBriefs[p.id]?.founderPedigreeHits} lang={lang} />
              <CardRow label={lang === 'en' ? 'Ops' : '运营'}>
                <OpsRow p={p} lang={lang} />
              </CardRow>
              {(view === 'web3' || wbCompany) && (
                <CardRow label={lang === 'en' ? 'Fund' : '融资'}>
                  {view === 'web3' && (isEF
                    ? (lang === 'en' ? 'Foundation grant · no token' : '基金会资助 · 未发币')
                    : isAccelerator
                      ? (lang === 'en' ? 'Accelerator portfolio · no public round disclosed' : '加速器 portfolio · 未披露公开轮次')
                      : (lang === 'en' ? 'Hackathon project · no funding record' : '黑客松项目 · 无融资记录'))}
                  <AiUnverifiedNote text={wbCompany} lang={lang} untranslated={wbCompanyUntranslated} t={t} />
                </CardRow>
              )}

              {(() => {
                const cm = contactMap[p.id];
                const btnBase = {
                  padding: '5px 12px', background: C.surface, border: `1px solid ${C.border}`,
                  borderRadius: 999, fontSize: 11, cursor: 'pointer', color: C.text,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                };
                return (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.faint}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: C.muted, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Lock size={11} strokeWidth={2} />
                        {user
                          ? (isAdmin
                              ? (lang === 'en' ? 'Contact · unlimited (admin)' : '联系方式 · 管理员不限次数')
                              : (lang === 'en' ? `Contact · ${creditsLeft} left` : `联系方式 · 剩 ${creditsLeft} 次`))
                          : (lang === 'en' ? 'Sign in for contact (3 free)' : '登录后可获取联系方式（3 次免费）')}
                      </span>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <button onClick={() => unlockContact(p)} style={{
                          ...btnBase, background: C.text, border: `1px solid ${C.text}`, color: '#fff', fontWeight: 500,
                        }}>
                          {cm?.status === 'loading'
                            ? (lang === 'en' ? 'Fetching…' : '获取中…')
                            : (lang === 'en' ? 'Get contact' : '获取联系方式')}
                        </button>
                        <button onClick={() => user
                          ? setTipModal({ project: p, contact: '', relationship: '', state: 'edit' })
                          : setHint(lang === 'en' ? 'Sign in to contribute contacts' : '请先登录再提交「我认识」')}
                          style={btnBase}>
                          <UserCheck size={11} strokeWidth={2} />{lang === 'en' ? 'I know them' : '我认识'}
                        </button>
                        <button onClick={() => toggleFollow(p.id)} style={{
                          ...btnBase, color: follows.has(p.id) ? '#b45309' : C.text,
                        }}>
                          <Star size={11} strokeWidth={2} fill={follows.has(p.id) ? '#f59e0b' : 'none'} />
                          {follows.has(p.id) ? (lang === 'en' ? 'Following' : '已关注') : (lang === 'en' ? 'Follow' : '关注')}
                        </button>
                        <button onClick={() => setWikiProject(p)} style={btnBase}>
                          {lang === 'en' ? 'View Wiki' : '查看档案'}
                        </button>
                      </span>
                    </div>
                    {cm?.status === 'shown' && (
                      <div style={{ marginTop: 8, padding: '8px 10px', background: C.faint, borderRadius: 6, fontSize: 11.5 }}>
                        {cm.contact.email && <div>Email：<a href={`mailto:${cm.contact.email}`}>{cm.contact.email}</a></div>}
                        {cm.contact.twitterUsername && <div>Twitter：<a href={`https://x.com/${cm.contact.twitterUsername}`} target="_blank" rel="noreferrer">@{cm.contact.twitterUsername}</a></div>}
                        {cm.contact.website && <div>{lang === 'en' ? 'Site' : '站点'}：<a href={/^https?:/.test(cm.contact.website) ? cm.contact.website : `https://${cm.contact.website}`} target="_blank" rel="noreferrer">{cm.contact.website}</a></div>}
                        {cm.contact.team?.length > 0 && (
                          <div>{lang === 'en' ? 'Team (LinkedIn)' : '团队（LinkedIn）'}：
                            {cm.contact.team.map((t, i) => (
                              <span key={t.linkedin}>
                                {i > 0 && '、'}
                                <a href={t.linkedin} target="_blank" rel="noreferrer">{t.name}{t.role ? `（${t.role}）` : ''}</a>
                              </span>
                            ))}
                          </div>
                        )}
                        {cm.contact.colosseumTeam?.length > 0 && (
                          <div>{lang === 'en' ? 'Team (Colosseum)' : '团队（Colosseum）'}：
                            {cm.contact.colosseumTeam.map((t, i) => (
                              <span key={t.profileUrl}>
                                {i > 0 && '、'}
                                <a href={t.profileUrl} target="_blank" rel="noreferrer">{t.handle}</a>
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ color: C.muted, fontSize: 10, marginTop: 4 }}>
                          {lang === 'en'
                            ? 'Source: GitHub / project website / Colosseum profile (self-disclosed)'
                            : '来源：GitHub / 项目官网 / Colosseum 主页（项目方自行公开的渠道）'}
                        </div>
                        {cm.wikiFounders && <WikiFoundersMention text={cm.wikiFounders} lang={lang} />}
                      </div>
                    )}
                    {cm?.status === 'none' && (
                      <div style={{ marginTop: 6, padding: '8px 10px', background: C.faint, borderRadius: 6, fontSize: 11 }}>
                        <div style={{ color: C.muted }}>
                          {lang === 'en'
                            ? 'No self-disclosed contact found (credit not used)'
                            : '未找到本人公开的联系方式（未扣额度）'}
                        </div>
                        {cm.wikiFounders && <WikiFoundersMention text={cm.wikiFounders} lang={lang} />}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>

      {/* 分页 */}
      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginTop: 14 }}>
          <button disabled={page === 0} onClick={() => setPage((n) => n - 1)} style={{
            padding: '4px 12px', borderRadius: 6, border: `1px solid ${C.border}`,
            background: C.surface, fontSize: 11, cursor: page === 0 ? 'default' : 'pointer',
            color: page === 0 ? '#a1a1aa' : C.text,
          }}>{lang === 'en' ? 'Prev' : '上一页'}</button>
          <span style={{ fontSize: 11, color: C.muted }}>
            {page + 1} / {totalPages} · {total.toLocaleString()} {lang === 'en' ? 'projects' : '个项目'}
          </span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage((n) => n + 1)} style={{
            padding: '4px 12px', borderRadius: 6, border: `1px solid ${C.border}`,
            background: C.surface, fontSize: 11, cursor: page + 1 >= totalPages ? 'default' : 'pointer',
            color: page + 1 >= totalPages ? '#a1a1aa' : C.text,
          }}>{lang === 'en' ? 'Next' : '下一页'}</button>
        </div>
      )}

      {/* 「我认识」提交弹窗 — 隐私红线：只收对方本人愿意公开的联系方式（见风险清单） */}
      {tipModal && (
        <div onClick={() => setTipModal(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: C.surface, borderRadius: 12, padding: '20px 22px', width: 420, maxWidth: '100%',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {lang === 'en' ? 'I know them' : '我认识'} · {tipModal.project.project_name}
              </span>
              <button onClick={() => setTipModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex' }} aria-label="close">
                <X size={15} strokeWidth={2} />
              </button>
            </div>
            <p style={{ fontSize: 11, color: C.muted, margin: '0 0 12px' }}>
              {lang === 'en' ? 'Contribute a contact; +1 credit after review' : '贡献联系方式，审核通过后获得 +1 额度'}
            </p>
            {tipModal.state === 'done' ? (
              <p style={{ fontSize: 12.5, margin: '8px 0' }}>
                {lang === 'en' ? 'Submitted. You will get +1 credit once approved.' : '已提交。审核通过后自动 +1 额度。'}
              </p>
            ) : (
              <>
                <label style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 4 }}>
                  {lang === 'en' ? 'Contact (WeChat / phone / email)' : '联系方式（微信 / 手机 / 邮箱）'}
                </label>
                <input value={tipModal.contact}
                  onChange={(e) => setTipModal((t) => ({ ...t, contact: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, marginBottom: 10 }} />
                <label style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 4 }}>
                  {lang === 'en' ? 'Relationship with founder (optional)' : '与创始人关系（选填）'}
                </label>
                <input value={tipModal.relationship}
                  onChange={(e) => setTipModal((t) => ({ ...t, relationship: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, marginBottom: 10 }} />
                <p style={{ fontSize: 10.5, color: '#854d0e', background: '#fef9c3', borderRadius: 6, padding: '7px 10px', margin: '0 0 12px', lineHeight: 1.5 }}>
                  {lang === 'en'
                    ? 'Only submit contact info the person has chosen to make public. Review verifies this; submissions of private info shared without consent will be rejected.'
                    : '提交前请确认：这是对方本人愿意公开的联系方式，不是未经同意分享的私人信息。审核会核实这一点，不符合的提交不会被采纳。'}
                </p>
                {tipModal.state === 'error' && (
                  <p style={{ fontSize: 11, color: '#dc2626', margin: '0 0 8px' }}>
                    {lang === 'en' ? 'Submit failed, try again' : '提交失败，请重试'}
                  </p>
                )}
                <button onClick={submitTip} disabled={tipModal.state === 'submitting'} style={{
                  width: '100%', padding: '8px 0', background: C.text, color: '#fff',
                  border: 'none', borderRadius: 999, fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
                }}>
                  {tipModal.state === 'submitting' ? (lang === 'en' ? 'Submitting…' : '提交中…') : (lang === 'en' ? 'Submit' : '提交')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {wikiProject && (
        <EarlyBirdWikiModal
          project={wikiProject}
          onClose={() => setWikiProject(null)}
          lang={lang}
          user={user}
          isAdmin={isAdmin}
          creditsLeft={creditsLeft}
          contactState={contactMap[wikiProject.id]}
          onUnlockContact={unlockContact}
          onOpenTipModal={(p) => setTipModal({ project: p, contact: '', relationship: '', state: 'edit' })}
          isFollowing={follows.has(wikiProject.id)}
          onToggleFollow={toggleFollow}
          notes={notes[wikiProject.id] || []}
          onAddNote={(note) => addNote(wikiProject.id, note)}
        />
      )}
    </div>
  );
}
